import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader, Disclaimer } from "@/components/Common";
import { DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";
import {
  ACTIVE_ROLES, CLEARANCE_STATUSES, READY_STATUSES, DOC_STATUSES, DOC_TYPES,
  LICENSE_REQUEST_STATUSES, SUBMISSION_STATUSES,
} from "@/lib/constants";
import { roleLabel, useAuth } from "@/lib/auth";
import { Save, UserPlus, ShieldCheck, Plug, BookOpen, Lock, MoreHorizontal, UserX, UserCheck, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { Link } from "react-router-dom";
import { ReasonModal } from "@/components/ReasonModal";
import { ROLE_CHANGE_REASONS, USER_DISABLE_REASONS } from "@/lib/lists";
import PendingPasswordResetsCard from "@/components/PendingPasswordResetsCard";

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [settings, setSettings] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  // Phase A Run 2 follow-up — reason-modal state for sensitive user actions.
  const [roleModal, setRoleModal] = useState(null);       // { uid, role } | null
  const [disableModal, setDisableModal] = useState(null); // { user, action } | null
  const [reasonBusy, setReasonBusy] = useState(false);

  async function confirmRoleChange({ category, detail }) {
    if (!roleModal) return;
    setReasonBusy(true);
    try {
      await api.put(`/auth/users/${roleModal.uid}/role`, {
        role: roleModal.role,
        reason_category: category,
        reason_detail: detail,
      });
      toast.success("Role updated. Audit record created.");
      setRoleModal(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReasonBusy(false); }
  }

  async function confirmDisableChange({ category, detail }) {
    if (!disableModal) return;
    setReasonBusy(true);
    try {
      const path = disableModal.action === "reactivate" ? "reactivate" : "deactivate";
      await api.put(`/auth/users/${disableModal.user.id}/${path}`, {
        reason_category: category,
        reason_detail: detail,
      });
      toast.success(`${disableModal.action === "reactivate" ? "Reactivated" : "Deactivated"} ${disableModal.user.email}. Audit record created.`);
      setDisableModal(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReasonBusy(false); }
  }

  async function load() {
    setLoading(true);
    try {
      const [u, r, s] = await Promise.all([
        api.get("/auth/users"),
        api.get("/roles"),
        api.get("/settings"),
      ]);
      setUsers(u.data);
      setRoles(r.data);
      setSettings(s.data);
      setEdits({});
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(()=>{ load(); }, []);

  async function changeRole(uid, role) {
    // Phase A Run 2 follow-up — sensitive role change requires reason capture.
    setRoleModal({ uid, role });
  }

  async function deactivateUser(u) {
    // Phase A Run 2 follow-up — sensitive user disable requires reason capture.
    setDisableModal({ user: u, action: "deactivate" });
  }
  async function reactivateUser(u) {
    // Reactivation also captured for symmetry (reuses USER_DISABLE_REASONS list).
    setDisableModal({ user: u, action: "reactivate" });
  }
  async function deleteUser(u, reason = "") {
    try {
      const qs = new URLSearchParams();
      qs.set("reason", reason || "No reason recorded");
      qs.set("force", "true");
      await api.delete(`/auth/users/${u.id}?${qs.toString()}`);
      toast.success(`Deleted ${u.email}`);
      load();
    }
    catch (e) { toast.error(errMsg(e)); }
  }

  // Active Super Admin count gate — surfaced in UI so the action menu can
  // disable destructive options against the last remaining active Super Admin.
  const activeSuperAdminCount = users.filter(u => u.role === "super_admin" && (u.is_active !== false)).length;

  async function saveSetting(sid) {
    const v = edits[sid];
    if (v === undefined) return;
    try { await api.put(`/settings/${sid}`, { setting_value: v }); toast.success("Setting saved"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  }

  // Group settings by category
  const grouped = settings.reduce((acc, s) => {
    const k = s.setting_category || "general";
    if (!acc[k]) acc[k] = [];
    acc[k].push(s);
    return acc;
  }, {});

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings / Admin"
        subtitle="Super Admin controls · users, roles, system settings, status values, security posture"
      />

      {/* Admin-area branding lockup */}
      <Card className="mb-6" data-testid="settings-branding-lockup">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <BrandLogo variant="gfmp" size="lg" withRing testId="settings-gfmp-logo" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl leading-tight">GFMP Command Center</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              The Durban 49 Group · GOOD FRIAR MUSIC PUBLISHING · Internal Alpha
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 max-w-xl leading-relaxed">
              GFMP serves as the operating and administrative authority for Kandake Prime. All
              compliance decisions, clearance approvals, and license workflows are executed by
              authorised internal personnel under GFMP oversight.
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.18em] font-medium self-start"
            style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
            data-testid="settings-powered-by">
            <BrandLogo variant="kandake" size="xs" ariaHidden />
            Powered by Kandake
          </div>
        </CardContent>
      </Card>

      {/* Personal account security — visible to any authenticated user
          who can reach Settings (today: Super Admin only). Affects ONLY
          the signed-in account; cannot be used to reset another user's
          password. */}
      <Card className="mb-6" data-testid="settings-security-card">
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className="w-10 h-10 rounded-full grid place-items-center shrink-0"
               style={{ background: "hsl(347 72% 22% / 0.08)", color: "hsl(347 72% 22%)" }}
               aria-hidden="true">
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg leading-tight">Your account security</div>
            <div className="text-[12.5px] text-muted-foreground mt-0.5 max-w-xl leading-relaxed">
              Change your own Kandake password. You will be signed out and asked to sign in again
              with your new password. This action affects only your account.
            </div>
          </div>
          <Button asChild data-testid="settings-change-password-button">
            <Link to="/account/security">
              <KeyRound className="w-4 h-4 mr-2" />
              Change password
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Create internal user (Super Admin only) */}
      <CreateUserCard onCreated={load} />

      {/* Pending Password Resets (Super Admin only) */}
      <PendingPasswordResetsCard />

      {/* Users */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-start justify-between gap-3">
            <div>
              <div className="font-display text-xl">Users</div>
              <div className="text-xs text-muted-foreground">Active role assignments. Industry Access is parked for a future phase and cannot be assigned here.</div>
            </div>
            <div className="text-[11px] text-muted-foreground hidden sm:block">
              Active Super Admins: <span className="font-medium text-foreground" data-testid="super-admin-count">{activeSuperAdminCount}</span>
            </div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="w-[220px]">Change Role</TableHead>
              <TableHead className="w-[60px] text-right"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && users.map(u => {
                const isSelf = u.id === user?.id;
                const isActive = u.is_active !== false;
                const isLastSuperAdmin = u.role === "super_admin" && isActive && activeSuperAdminCount <= 1;
                return (
                <TableRow key={u.id} data-testid={`settings-user-${u.id}`} className={isActive ? "" : "opacity-60"}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>{roleLabel(u.role)}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-[11px] ${isActive ? "status-client" : "status-incomplete"}`}
                      data-testid={`user-status-${u.id}`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{(u.last_login || "").slice(0,16).replace("T"," ") || "—"}</TableCell>
                  <TableCell>
                    <Select
                      value={u.role}
                      onValueChange={(v)=>changeRole(u.id, v)}
                      disabled={isSelf || !isActive || (isLastSuperAdmin)}
                    >
                      <SelectTrigger data-testid={`role-select-${u.id}`}><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {ACTIVE_ROLES.concat(["super_admin"]).filter((v,i,a)=>a.indexOf(v)===i).map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <UserActionsMenu
                      target={u}
                      isSelf={isSelf}
                      isActive={isActive}
                      isLastSuperAdmin={isLastSuperAdmin}
                      onDeactivate={() => deactivateUser(u)}
                      onReactivate={() => reactivateUser(u)}
                      onDelete={(reason) => deleteUser(u, reason)}
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && (
            <div className="px-5 py-2 text-[11px] text-muted-foreground border-t" data-testid="user-mgmt-rules">
              You cannot deactivate or delete your own account. The last remaining active Super Admin cannot be deactivated, deleted, or demoted. All actions are recorded in the audit log.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Roles directory (read-only) */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b">
            <div className="font-display text-xl">Roles Directory</div>
            <div className="text-xs text-muted-foreground">Active roles drive permissions in this build. Future-role placeholders are reserved and intentionally inactive.</div>
          </div>
          <Table className="table-fixed w-full">
            <TableHeader><TableRow>
              <TableHead className="w-[160px]">Role ID</TableHead>
              <TableHead className="w-[200px]">Role Name</TableHead>
              <TableHead className="w-[120px]">Active</TableHead>
              <TableHead>Permissions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {roles.map(r => (
                <TableRow key={r.role_id} data-testid={`role-row-${r.role_id}`} className="align-top">
                  <TableCell className="font-mono text-xs break-all">{r.role_id}</TableCell>
                  <TableCell className="break-words">{r.role_name}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[11px] ${r.is_active ? "status-client" : "status-incomplete"}`}>{r.is_active ? "Active" : "Future / Inactive"}</span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {(r.permissions || []).length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1" data-testid={`role-permissions-${r.role_id}`}>
                        {(r.permissions || []).map((p) => (
                          <span
                            key={p}
                            className="inline-block px-1.5 py-0.5 rounded font-mono text-[10px] break-all"
                            style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b">
            <div className="font-display text-xl">System Settings</div>
            <div className="text-xs text-muted-foreground">Internal configuration. Only Super Admin can edit. Changes are audit-logged. Long values are wrapped automatically.</div>
          </div>
          {Object.keys(grouped).sort().map(cat => (
            <div key={cat}>
              <div className="px-5 pt-4 pb-2 text-[11px] uppercase tracking-wide text-muted-foreground">{cat}</div>
              <Table className="table-fixed w-full">
                <TableHeader><TableRow>
                  <TableHead className="w-[240px]">Setting</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="w-[160px]">Updated by</TableHead>
                  <TableHead className="w-[110px]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {grouped[cat].map(s => {
                    const editing = edits[s.setting_id] !== undefined;
                    const value = editing ? edits[s.setting_id] : s.setting_value;
                    const longValue = typeof value === "string" && (value.length > 60 || value.includes("\n"));
                    return (
                      <TableRow key={s.setting_id} data-testid={`setting-row-${s.setting_id}`} className="align-top">
                        <TableCell className="font-mono text-xs pt-3 break-all">{s.setting_name}</TableCell>
                        <TableCell className="pt-2">
                          {longValue ? (
                            <Textarea
                              value={value || ""}
                              onChange={(e) => setEdits(prev => ({ ...prev, [s.setting_id]: e.target.value }))}
                              rows={Math.min(6, Math.max(2, Math.ceil((value || "").length / 80)))}
                              className="w-full text-sm font-mono"
                              data-testid={`setting-input-${s.setting_name}`}
                            />
                          ) : (
                            <Input
                              value={value || ""}
                              onChange={(e) => setEdits(prev => ({ ...prev, [s.setting_id]: e.target.value }))}
                              className="h-9 w-full"
                              data-testid={`setting-input-${s.setting_name}`}
                            />
                          )}
                          {editing && (
                            <div className="text-[11px] mt-1" style={{ color: "hsl(28 80% 38%)" }} data-testid={`setting-dirty-${s.setting_name}`}>
                              Unsaved change — click Save to persist.
                            </div>
                          )}
                          {s.setting_description && (
                            <div className="text-[11px] text-muted-foreground mt-1 break-words">{s.setting_description}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground pt-3 break-all">
                          {s.updated_by || "—"}
                          {s.updated_at ? (
                            <div className="font-mono text-[10px] mt-0.5">{(s.updated_at || "").slice(0, 16).replace("T", " ")}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="pt-3">
                          <Button size="sm" variant="outline" disabled={!editing} onClick={() => saveSetting(s.setting_id)} data-testid={`setting-save-${s.setting_name}`}>
                            <Save className="w-3.5 h-3.5 mr-1.5" /> Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>

      <Disclaimer>Future placeholders: Legal Review, Beta Tester, Industry Access — not active in Internal Alpha.</Disclaimer>

      {/* Status Values reference */}
      <Card className="mb-6 mt-2">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <BookOpen className="w-4 h-4" style={{color:"hsl(347 72% 22%)"}} />
            <div>
              <div className="font-display text-xl">Status Values</div>
              <div className="text-xs text-muted-foreground">Canonical enumerations recognised by the platform. Reference only — values are defined in code.</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5" data-testid="status-values-grid">
            <EnumBlock title="Clearance Status" items={CLEARANCE_STATUSES} extra={`Ready tier: ${READY_STATUSES.join(", ")}`} />
            <EnumBlock title="Risk Level" items={["Low","Medium","High","Critical"]} />
            <EnumBlock title="Document Status" items={DOC_STATUSES} />
            <EnumBlock title="Document Type" items={DOC_TYPES} />
            <EnumBlock title="License Request Status" items={LICENSE_REQUEST_STATUSES} />
            <EnumBlock title="Submission Status" items={SUBMISSION_STATUSES} />
          </div>
        </CardContent>
      </Card>

      {/* Future Connector Settings */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Plug className="w-4 h-4" style={{color:"hsl(347 72% 22%)"}} />
            <div>
              <div className="font-display text-xl">Future Connector Settings</div>
              <div className="text-xs text-muted-foreground">Catalog-agnostic by design. The connector layer is scaffolded but not active in Internal Alpha.</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5" data-testid="future-connectors-grid">
            {[
              {name: "Supabase connector",        note: "Internal Alpha runs on MongoDB; canonical fields map 1:1 to Supabase tables when activated."},
              {name: "CSV / XLSX import",         note: "Bulk import for catalog records."},
              {name: "Google Sheets import",      note: "Live-sync from approved sheets."},
              {name: "Airtable import",           note: "Read-only mirror of partner catalogs."},
              {name: "API connector",             note: "Generic REST connector for partner systems."},
              {name: "DDEX mapping connector",    note: "Industry-standard rights/release messaging."},
              {name: "Proprietary database",      note: "Bespoke partner database bridges."},
              {name: "Read-only partner",         note: "Restricted catalog access for vetted partners."},
              {name: "Manual upload",             note: "Audited fallback for one-off intakes."},
            ].map((c) => (
              <div key={c.name} className="rounded-md border bg-secondary/40 p-3" data-testid={`connector-${c.name}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{c.name}</div>
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Future</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{c.note}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Security Notes */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Lock className="w-4 h-4" style={{color:"hsl(347 72% 22%)"}} />
            <div>
              <div className="font-display text-xl">Security Notes</div>
              <div className="text-xs text-muted-foreground">Posture statements for Internal Alpha. Operational; not exhaustive.</div>
            </div>
          </div>
          <ul className="p-5 text-sm space-y-2" data-testid="security-notes-list">
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>Private by default.</strong> Every internal endpoint requires a valid JWT. There are no public dashboard, catalog, or document routes.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>Role-based access control.</strong> Super Admin, GFMP Reviewer, and Catalog Manager are active; Legal Review, Beta Tester, and Industry Access are reserved future roles and intentionally inactive.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>No public sign-up.</strong> New internal users must be created by a Super Admin from this page.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>Audit trail.</strong> Every state change writes an immutable row to the audit log, including sign-in and role changes.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>Documents are internal by default.</strong> File download endpoints require an authenticated session and short-lived token credentials.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>No backend keys in frontend code.</strong> Secrets remain server-side; the frontend only holds a session JWT.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" style={{color:"hsl(150 50% 30%)"}}/><span><strong>Compliance support only.</strong> Kandake Prime is not a law firm and does not provide legal advice. Human review is required before any external submission.</span></li>
          </ul>
        </CardContent>
      </Card>

      <ReasonModal
        open={!!roleModal}
        onOpenChange={(v) => { if (!v) setRoleModal(null); }}
        title="Change User Role"
        description={roleModal ? `Reason is required for role changes. This becomes part of the permanent audit record.` : ""}
        categoryOptions={ROLE_CHANGE_REASONS}
        confirmLabel="Change Role"
        severity="high"
        busy={reasonBusy}
        testid="role-change-modal"
        onConfirm={confirmRoleChange}
      />

      <ReasonModal
        open={!!disableModal}
        onOpenChange={(v) => { if (!v) setDisableModal(null); }}
        title={disableModal?.action === "reactivate" ? "Reactivate User" : "Deactivate User"}
        description={disableModal ? `Reason is required. The user record and its full audit history are preserved regardless of state.` : ""}
        categoryOptions={USER_DISABLE_REASONS}
        confirmLabel={disableModal?.action === "reactivate" ? "Reactivate" : "Deactivate"}
        severity="high"
        busy={reasonBusy}
        testid="user-disable-modal"
        onConfirm={confirmDisableChange}
      />
    </div>
  );
}

function EnumBlock({ title, items, extra }) {
  return (
    <div className="rounded-md border bg-secondary/40 p-3">
      <div className="text-sm font-medium">{title}</div>
      <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
        {items.map((v) => <li key={v} className="font-mono">{v}</li>)}
      </ul>
      {extra && <div className="text-[11px] text-muted-foreground mt-2">{extra}</div>}
    </div>
  );
}

function UserActionsMenu({ target, isSelf, isActive, isLastSuperAdmin, onDeactivate, onReactivate, onDelete }) {
  const [confirmKind, setConfirmKind] = useState(null); // "delete" | "deactivate" | null
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteOther, setDeleteOther] = useState("");
  const blockReason = isSelf
    ? "You cannot modify your own account."
    : isLastSuperAdmin
    ? "Cannot remove the last remaining active Super Admin."
    : "";
  const disabled = isSelf || isLastSuperAdmin;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            data-testid={`user-actions-${target.id}`}
            aria-label="User actions"
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          <DropdownMenuLabel>Account actions</DropdownMenuLabel>
          {blockReason && (
            <div className="px-2 pb-1 text-[11px] text-muted-foreground">{blockReason}</div>
          )}
          <DropdownMenuSeparator />
          {isActive ? (
            <DropdownMenuItem
              disabled={disabled}
              onClick={() => setConfirmKind("deactivate")}
              data-testid={`user-deactivate-${target.id}`}
            >
              <UserX className="w-4 h-4 mr-2" /> Deactivate account
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled={isSelf}
              onClick={onReactivate}
              data-testid={`user-reactivate-${target.id}`}
            >
              <UserCheck className="w-4 h-4 mr-2" /> Reactivate account
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={disabled}
            onClick={() => setConfirmKind("delete")}
            data-testid={`user-delete-${target.id}`}
            className="text-[hsl(0_70%_42%)] focus:text-[hsl(0_70%_42%)]"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete account
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirmKind} onOpenChange={(o) => { if (!o) { setConfirmKind(null); setDeleteReason(""); setDeleteOther(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === "delete" ? `Delete ${target.email}?` : `Deactivate ${target.email}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === "delete"
                ? "The user row will be removed. Audit history of their prior actions is preserved on each affected record. This action cannot be undone."
                : "The user will no longer be able to sign in. You can reactivate them later. Audit history is preserved."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmKind === "delete" && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Reason (required · captured in audit)
                </Label>
                <Select value={deleteReason} onValueChange={setDeleteReason}>
                  <SelectTrigger className="mt-1 text-[12.5px]" data-testid={`user-delete-reason-trigger-${target.id}`}>
                    <SelectValue placeholder="Select a reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELETE_REASON_CHOICES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {deleteReason === "Other" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Explanation (required when reason is Other)
                  </Label>
                  <Textarea
                    value={deleteOther}
                    onChange={(e) => setDeleteOther(e.target.value)}
                    rows={2}
                    placeholder="Briefly explain…"
                    className="mt-1 text-[12.5px]"
                    data-testid={`user-delete-reason-other-${target.id}`}
                  />
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-confirm-action"
              disabled={confirmKind === "delete" && (!deleteReason || (deleteReason === "Other" && (deleteOther || "").trim().length < 3))}
              onClick={() => {
                if (confirmKind === "delete") {
                  const finalReason = deleteReason === "Other" ? `Other: ${deleteOther.trim()}` : deleteReason;
                  onDelete(finalReason);
                }
                else if (confirmKind === "deactivate") onDeactivate();
                setConfirmKind(null);
                setDeleteReason("");
                setDeleteOther("");
              }}
              style={{ background: confirmKind === "delete" ? "hsl(0 70% 42%)" : "hsl(347 72% 22%)", color: "white" }}
            >
              {confirmKind === "delete" ? "Delete account" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CreateUserCard({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("gfmp_reviewer");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/signup", { name, email, password, role });
      toast.success(`Internal user created: ${email}`);
      setName(""); setEmail(""); setPassword(""); setRole("gfmp_reviewer"); setOpen(false);
      onCreated && onCreated();
    } catch (err) {
      toast.error(errMsg(err, "Could not create user"));
    } finally { setBusy(false); }
  }
  return (
    <Card className="mb-6">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" style={{color:"hsl(347 72% 22%)"}} />
            <div>
              <div className="font-display text-xl">Create internal user</div>
              <div className="text-xs text-muted-foreground">Super Admin only. New accounts are created internally — there is no public sign-up.</div>
            </div>
          </div>
          {!open && <Button onClick={()=>setOpen(true)} data-testid="settings-open-create-user" style={{background:"hsl(347 72% 22%)"}}><UserPlus className="w-4 h-4 mr-2"/>New user</Button>}
        </div>
        {open && (
          <form onSubmit={submit} className="mt-4 grid md:grid-cols-4 gap-3 items-end" data-testid="settings-create-user-form">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input value={name} onChange={(e)=>setName(e.target.value)} required data-testid="create-user-name"/>
            </div>
            <div>
              <Label className="text-xs">Work email</Label>
              <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="create-user-email"/>
            </div>
            <div>
              <Label className="text-xs">Temporary password (≥6)</Label>
              <Input type="password" minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="create-user-password"/>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="create-user-role"><SelectValue/></SelectTrigger>
                <SelectContent>{ACTIVE_ROLES.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="create-user-submit" style={{background:"hsl(347 72% 22%)"}}>{busy ? "Creating…" : "Create user"}</Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
